import { useState, type FocusEvent, type PropsWithChildren } from "react";
import classNames from "classnames";
import KeyboardShortcutHint, { type KeyboardShortcutHintProps } from "./KeyboardShortcutHint";

export type ActionShortcutTooltipProps = PropsWithChildren<{
  /** Visible action name in the hover tooltip (for example `Find` or `Add`). */
  label: string;
  /** Shortcut pills shown after the label. `tone` defaults to `contrast` for the dark tooltip. */
  hints: KeyboardShortcutHintProps[];
}>;

/**
 * Hover/focus hint for icon-only toolbar buttons. Intentionally not `Tooltip`:
 * that control opens `md-menu`, which `hasTransientPopupOpen()` treats as a
 * blocking popup and which wraps the child in `role="button"`.
 */
export default function ActionShortcutTooltip({ label, hints, children }: ActionShortcutTooltipProps) {
  const [open, setOpen] = useState(false);

  const handleBlurCapture = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setOpen(false);
    }
  };

  return (
    <div
      className="relative inline-flex cursor-pointer [&_*]:cursor-pointer"
      data-testid="action-shortcut-tooltip"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocusCapture={() => setOpen(true)}
      onBlurCapture={handleBlurCapture}
      onClick={() => setOpen(false)}
    >
      {children}
      {open ? (
        <div
          role="tooltip"
          className="absolute left-1/2 top-full z-50 w-fit -translate-x-1/2 cursor-pointer select-none pt-1"
        >
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-[4px] bg-sys-brand-on-surface px-3 py-1.5 text-label-medium text-sys-brand-surface shadow-md">
            {label}
            {hints.map((hint, index) => (
              <KeyboardShortcutHint
                key={`${hint.keyLabel}-${index}`}
                {...hint}
                tone={hint.tone ?? "contrast"}
                size={hint.size ?? "default"}
                className={classNames("!inline-flex", hint.className)}
              />
            ))}
          </span>
        </div>
      ) : null}
    </div>
  );
}
